import {
  useGetCircuitsForAgencies,
  useShareCircuitsWithAgencies,
} from '@/hooks/useDistributors';
import Modal from '../../ui/Modal';
import Loader from '../../ui/Loader';
import { useForm, useWatch } from 'react-hook-form';
import { LabelValue } from '@/types/global';
import { useGetCountriesWithCircuits } from '@/hooks/useCircuits';
import StyledInput from '../../styled/StyledInput';
import StyledSelect from '../../styled/StyledSelect';
import StyledBooleanCheckbox from '../../styled/StyledBooleanCheckbox';
import SearchIcon from '@/assets/icons/search.svg?react';
import { useMemo, useState } from 'react';
import { RowSelectionState } from '@tanstack/react-table';
import CircuitSelectionTable from './CircuitSelectionTable';
import { useTranslation } from '@/i18n';

interface SelectCircuitsModalProps {
  agencies: string[];
  networks?: string[];
  closeModal: () => void;
  onSuccess: () => void;
  shouldShareFutureCircuitsWithAllSelectedAgencies: boolean;
}

const SelectCircuitsModal = ({
  agencies,
  closeModal,
  onSuccess,
  shouldShareFutureCircuitsWithAllSelectedAgencies,
  networks,
}: SelectCircuitsModalProps) => {
  const { t } = useTranslation();
  const { control } = useForm<{
    circuitName: string;
    countries?: LabelValue<string>[];
    shouldShareAllCurrentCircuits: boolean;
    shouldShareAllFutureCircuits: boolean;
  }>({
    mode: 'onChange',
    defaultValues: {
      circuitName: '',
      countries: [],
      shouldShareAllCurrentCircuits: false,
      shouldShareAllFutureCircuits:
        shouldShareFutureCircuitsWithAllSelectedAgencies,
    },
  });

  const searchValue = useWatch({ control, name: 'circuitName' });
  const countriesValue = useWatch({ control, name: 'countries' });
  const shouldShareAllCurrentCircuits = useWatch({
    control,
    name: 'shouldShareAllCurrentCircuits',
  });
  const shouldShareAllFutureCircuits = useWatch({
    control,
    name: 'shouldShareAllFutureCircuits',
  });

  const { data: countriesOptions } = useGetCountriesWithCircuits({
    onlyPublished: true,
    select: (countries) =>
      countries.map(({ country }) => ({ label: country, value: country })),
  });

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const selectedCircuits = useMemo(
    () => Object.keys(rowSelection),
    [rowSelection],
  );

  const {
    data: circuitsWithAgenciesResponse,
    isSuccess,
    isLoading,
  } = useGetCircuitsForAgencies(agencies, networks);

  const { mutateAsync: shareCircuitsWithAgencies } =
    useShareCircuitsWithAgencies();

  const filteredCircuits = useMemo(
    () =>
      circuitsWithAgenciesResponse?.circuits
        .filter((circuit) =>
          circuit.name.toLowerCase().includes(searchValue.toLowerCase()),
        )
        .filter((circuit) =>
          countriesValue && countriesValue.length
            ? circuit.countries.some((country) =>
                countriesValue.map(({ value }) => value).includes(country),
              )
            : true,
        ) ?? [],
    [circuitsWithAgenciesResponse, countriesValue, searchValue],
  );

  return (
    <Modal
      title={t('distributors.modals.circuitSelection.title')}
      actionOnClick={() => {
        shareCircuitsWithAgencies({
          circuitIds: selectedCircuits,
          distributorIds: agencies,
          shouldShareAllCurrentCircuits,
          shouldShareAllFutureCircuits,
        });
        onSuccess();
      }}
      closeModal={closeModal}
      actionTitle={t('distributors.modals.circuitSelection.actionTitle')}
    >
      {isLoading ? (
        <Loader />
      ) : (
        isSuccess && (
          <div className="flex flex-col gap-4">
            <div className="flex gap-4">
              <StyledInput
                control={control}
                name="circuitName"
                placeholder={t(
                  'distributors.modals.circuitSelection.searchCircuitPlaceholder',
                )}
                icon={<SearchIcon className="w-5 h-5 stroke-gray-500" />}
              />
              <StyledSelect
                control={control}
                name="countries"
                options={countriesOptions}
                placeholder={t(
                  'distributors.modals.circuitSelection.countriesPlaceholder',
                )}
                isMulti
              />
            </div>
            <div className="flex gap-4 w-full">
              <div className="w-full">
                <StyledBooleanCheckbox
                  control={control}
                  name="shouldShareAllCurrentCircuits"
                  optionLabel={t(
                    'distributors.modals.circuitSelection.shareAllCurrentCircuits',
                  )}
                  className="text-gray-700"
                />
              </div>
              <div className="w-full">
                <StyledBooleanCheckbox
                  control={control}
                  name="shouldShareAllFutureCircuits"
                  optionLabel={t(
                    'distributors.modals.circuitSelection.shareAllFutureCircuits',
                  )}
                  className="text-gray-700"
                />
              </div>
            </div>
            <CircuitSelectionTable
              filteredCircuits={filteredCircuits}
              agencies={circuitsWithAgenciesResponse.agencies}
              setSelectedCircuitRows={setRowSelection}
              shouldSelectAll={shouldShareAllCurrentCircuits}
            />
          </div>
        )
      )}
    </Modal>
  );
};

export default SelectCircuitsModal;
